import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2,
  CircleAlert,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { NotificationContext } from './notification-context';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';
export type NotificationPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight';
export type NotificationKey = string | number;

export type NotificationOptions = {
  title: ReactNode;
  description?: ReactNode;
  type?: NotificationType;
  key?: NotificationKey;
  /** Display time in seconds. Use 0 to keep the notification open. */
  duration?: number;
  placement?: NotificationPlacement;
  icon?: ReactNode;
  actions?: ReactNode;
  closable?: boolean;
  closeIcon?: ReactNode;
  className?: string;
  role?: 'alert' | 'status';
  onClick?: () => void;
  onClose?: () => void;
};

export type NotificationClose = () => void;

export type NotificationApi = {
  open: (options: NotificationOptions) => NotificationClose;
  success: (options: NotificationOptions) => NotificationClose;
  error: (options: NotificationOptions) => NotificationClose;
  info: (options: NotificationOptions) => NotificationClose;
  warning: (options: NotificationOptions) => NotificationClose;
  destroy: (key?: NotificationKey) => void;
};

export type NotificationProviderProps = {
  children: ReactNode;
  /** Default display time in seconds. */
  duration?: number;
  maxCount?: number;
  placement?: NotificationPlacement;
  offset?: number;
};

type NotificationItem = Required<
  Pick<NotificationOptions, 'key' | 'type' | 'title' | 'placement'>
> &
  Omit<NotificationOptions, 'key' | 'type' | 'title' | 'placement'> & {
    state: 'open' | 'closing';
  };

const NOTIFICATION_EXIT_DURATION_MS = 200;

const typeStyles: Record<NotificationType, string> = {
  success: 'text-success',
  error: 'text-destructive',
  info: 'text-info',
  warning: 'text-warning',
};

const typeIcons: Record<NotificationType, LucideIcon> = {
  success: CheckCircle2,
  error: CircleAlert,
  info: Info,
  warning: TriangleAlert,
};

const placements: NotificationPlacement[] = [
  'top',
  'topLeft',
  'topRight',
  'bottom',
  'bottomLeft',
  'bottomRight',
];

const placementStyles: Record<NotificationPlacement, string> = {
  top: 'inset-x-0 top-0 items-center',
  topLeft: 'left-0 top-0 items-start',
  topRight: 'right-0 top-0 items-end',
  bottom: 'inset-x-0 bottom-0 items-center',
  bottomLeft: 'bottom-0 left-0 items-start',
  bottomRight: 'bottom-0 right-0 items-end',
};

const motionStyles: Record<NotificationPlacement, string> = {
  top: 'data-[state=open]:slide-in-from-top-3 data-[state=closing]:slide-out-to-top-3',
  topLeft:
    'data-[state=open]:slide-in-from-left-3 data-[state=closing]:slide-out-to-left-3',
  topRight:
    'data-[state=open]:slide-in-from-right-3 data-[state=closing]:slide-out-to-right-3',
  bottom:
    'data-[state=open]:slide-in-from-bottom-3 data-[state=closing]:slide-out-to-bottom-3',
  bottomLeft:
    'data-[state=open]:slide-in-from-left-3 data-[state=closing]:slide-out-to-left-3',
  bottomRight:
    'data-[state=open]:slide-in-from-right-3 data-[state=closing]:slide-out-to-right-3',
};

export function NotificationProvider({
  children,
  duration = 4.5,
  maxCount = 5,
  placement = 'topRight',
  offset = 24,
}: NotificationProviderProps) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const itemsRef = useRef<NotificationItem[]>([]);
  const timersRef = useRef(new Map<NotificationKey, number>());
  const exitTimersRef = useRef(new Map<NotificationKey, number>());
  const nextKeyRef = useRef(0);

  const updateItems = useCallback((next: NotificationItem[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  const close = useCallback(
    (key: NotificationKey) => {
      const timer = timersRef.current.get(key);
      if (timer !== undefined) window.clearTimeout(timer);
      timersRef.current.delete(key);

      const item = itemsRef.current.find((candidate) => candidate.key === key);
      if (!item || item.state === 'closing') return;

      updateItems(
        itemsRef.current.map((candidate) =>
          candidate.key === key
            ? { ...candidate, state: 'closing' }
            : candidate,
        ),
      );
      exitTimersRef.current.set(
        key,
        window.setTimeout(() => {
          exitTimersRef.current.delete(key);
          const closingItem = itemsRef.current.find(
            (candidate) => candidate.key === key,
          );
          if (!closingItem || closingItem.state !== 'closing') return;
          updateItems(
            itemsRef.current.filter((candidate) => candidate.key !== key),
          );
          closingItem.onClose?.();
        }, NOTIFICATION_EXIT_DURATION_MS),
      );
    },
    [updateItems],
  );

  const destroy = useCallback(
    (key?: NotificationKey) => {
      if (key !== undefined) {
        close(key);
        return;
      }
      for (const timer of timersRef.current.values())
        window.clearTimeout(timer);
      timersRef.current.clear();
      for (const item of itemsRef.current) close(item.key);
    },
    [close],
  );

  useEffect(
    () => () => {
      for (const timer of timersRef.current.values())
        window.clearTimeout(timer);
      for (const timer of exitTimersRef.current.values())
        window.clearTimeout(timer);
    },
    [],
  );

  const open = useCallback(
    (options: NotificationOptions): NotificationClose => {
      const key = options.key ?? `notification-${nextKeyRef.current++}`;
      const item: NotificationItem = {
        key,
        title: options.title,
        type: options.type ?? 'info',
        placement: options.placement ?? placement,
        state: 'open',
        ...(options.description !== undefined
          ? { description: options.description }
          : {}),
        ...(options.duration !== undefined
          ? { duration: options.duration }
          : {}),
        ...(options.icon !== undefined ? { icon: options.icon } : {}),
        ...(options.actions !== undefined ? { actions: options.actions } : {}),
        ...(options.closable !== undefined
          ? { closable: options.closable }
          : {}),
        ...(options.closeIcon !== undefined
          ? { closeIcon: options.closeIcon }
          : {}),
        ...(options.className !== undefined
          ? { className: options.className }
          : {}),
        ...(options.role !== undefined ? { role: options.role } : {}),
        ...(options.onClick !== undefined ? { onClick: options.onClick } : {}),
        ...(options.onClose !== undefined ? { onClose: options.onClose } : {}),
      };

      const exitTimer = exitTimersRef.current.get(key);
      if (exitTimer !== undefined) window.clearTimeout(exitTimer);
      exitTimersRef.current.delete(key);

      const existingIndex = itemsRef.current.findIndex(
        (candidate) => candidate.key === key,
      );
      let next =
        existingIndex === -1
          ? [...itemsRef.current, item]
          : itemsRef.current.map((candidate) =>
              candidate.key === key ? item : candidate,
            );

      const openItems = next.filter((candidate) => candidate.state === 'open');
      if (openItems.length > maxCount) {
        const removed = openItems.slice(0, openItems.length - maxCount);
        updateItems(next);
        for (const candidate of removed) close(candidate.key);
        next = itemsRef.current;
      }
      updateItems(next);

      const previousTimer = timersRef.current.get(key);
      if (previousTimer !== undefined) window.clearTimeout(previousTimer);
      const displayDuration = options.duration ?? duration;
      if (displayDuration > 0) {
        timersRef.current.set(
          key,
          window.setTimeout(() => close(key), displayDuration * 1000),
        );
      } else {
        timersRef.current.delete(key);
      }

      return () => close(key);
    },
    [close, duration, maxCount, placement, updateItems],
  );

  const api = useMemo<NotificationApi>(() => {
    const typed = (type: NotificationType) =>
      (options: NotificationOptions) =>
        open({ ...options, type });

    return {
      open,
      success: typed('success'),
      error: typed('error'),
      info: typed('info'),
      warning: typed('warning'),
      destroy,
    };
  }, [destroy, open]);

  return (
    <NotificationContext value={api}>
      {children}
      {createPortal(
        <>
          {placements.map((currentPlacement) => {
            const placementItems = items.filter(
              (item) => item.placement === currentPlacement,
            );
            if (placementItems.length === 0) return null;

            const isBottom = currentPlacement.startsWith('bottom');
            return (
              <div
                key={currentPlacement}
                aria-live="polite"
                className={cn(
                  'pointer-events-none fixed z-100 flex flex-col gap-3 px-4',
                  placementStyles[currentPlacement],
                  isBottom && 'flex-col-reverse',
                )}
                style={isBottom ? { bottom: offset } : { top: offset }}
              >
                {placementItems.map((item) => {
                  const Icon = typeIcons[item.type];
                  const isClickable = item.onClick !== undefined;
                  return (
                    <div
                      key={item.key}
                      role={item.role ?? 'alert'}
                      data-state={item.state}
                      onClick={item.onClick}
                      className={cn(
                        'pointer-events-auto relative grid w-[min(24rem,calc(100vw-2rem))] grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 rounded-xl border border-border/70 bg-popover p-4 text-popover-foreground shadow-xl duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closing]:animate-out data-[state=closing]:fade-out-0 data-[state=closing]:zoom-out-95 data-[state=closing]:pointer-events-none motion-reduce:animate-none',
                        motionStyles[currentPlacement],
                        isClickable && 'cursor-pointer',
                        item.className,
                      )}
                    >
                      <span className={cn('row-span-2 mt-0.5', typeStyles[item.type])}>
                        {item.icon ?? (
                          <Icon className="size-5" aria-hidden="true" />
                        )}
                      </span>
                      <div className="min-w-0 text-sm font-semibold leading-snug">
                        {item.title}
                      </div>
                      {item.closable !== false ? (
                        <button
                          type="button"
                          aria-label="关闭通知"
                          onClick={(event) => {
                            event.stopPropagation();
                            close(item.key);
                          }}
                          className="-mr-1 -mt-1 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {item.closeIcon ?? (
                            <X className="size-4" aria-hidden="true" />
                          )}
                        </button>
                      ) : (
                        <span />
                      )}
                      {item.description !== undefined ? (
                        <div className="col-start-2 mt-1 min-w-0 text-sm leading-relaxed text-muted-foreground">
                          {item.description}
                        </div>
                      ) : null}
                      {item.actions !== undefined ? (
                        <div className="col-start-2 col-end-4 mt-3 flex flex-wrap justify-end gap-2">
                          {item.actions}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>,
        document.body,
      )}
    </NotificationContext>
  );
}
